import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { InputField, Form } from 'ht-styleguide';
import { anyCharacter, currencyWithOptionals, numberValidation } from '@constants/validations';
import useHomeHubAnswerController from '@scenes/HomeHub/hooks/hook.answerController';
import { ANSWER_SELECTOR_TYPES } from '@scenes/HomeHub/homehub.constants';
import { useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';
import AddressAutocomplete from '@components/UI/AddressAutocomplete';
import Maybe from '@components/Maybe';
import { isServerSide } from '@utils/environment';
import styles from '@scenes/HomeHub/components/Selectors/selectors.module.scss';

type TInputProps = {
  inputCallback?: BaseAnyFunction;
  inputType: string;
};
/**
 * MANUAL VALIDATION:
 * Using Yup/React Hook Form and Refs (this mostly) does not play nicely with react-spring.
 *   - The ref references in a transitioning element janks the animation. Adding the "animationFinished" does
 *   - help. Well, it does a little, but there is still a little jank. So, lets just manually deal with it here.
 */
/* Map the Input Type to the RegExp Type */
const inputTypeToRegExpType = {
  currency: {
    regex: currencyWithOptionals,
    message: 'Please enter a valid currency amount (ex: 554, 1,000, 12345)',
  },
  text: {
    regex: anyCharacter,
    message: 'Field cannot be empty',
  },
  address: {
    regex: anyCharacter,
    message: 'Field cannot be empty',
  },
  number: {
    regex: numberValidation,
    message: 'Please enter a valid number',
  },
  default: {
    regex: anyCharacter,
    message: 'Field cannot be empty',
  },
};

const RegularInputSelector = ({ inputType }: TInputProps) => {
  /* Hooks */
  const { selectedAnswers, answerSchemaNormalizer, required } = useHomeHubAnswerController();
  /* Pre-seeding */
  const [{ data_value: serverSeedDataValue = '' } = {}] = selectedAnswers;

  /* Local State */
  const [errorMessage, setErrorMessage] = useState('');
  const [inputEntry, setInputEntry] = useState(serverSeedDataValue);

  /* Store */
  const { setSelectedAnswersClientTransient, setHasDirtyAnswerSelections } = useHomeHubStoreActions();

  /* Constants */
  const [, validationInputType = 'text'] = inputType.split('_');
  const validationSchema = (inputTypeToRegExpType as Record<string, any>)[validationInputType]
    ? (inputTypeToRegExpType as Record<string, any>)[validationInputType]
    : inputTypeToRegExpType.default;

  /* --------- Methods --------- */
  const onHandleInputChange = (inputValue: string) => {
    const isValidInputEntry = required ? validationSchema.regex.test(inputValue) : true;

    setErrorMessage('');
    if (!isValidInputEntry) {
      setErrorMessage(validationSchema.message);
    }

    const formatAnswer = answerSchemaNormalizer.selectedAnswers(selectedAnswers).write(inputValue);
    setInputEntry(inputValue);
    setSelectedAnswersClientTransient(formatAnswer);
    setHasDirtyAnswerSelections(true);

    return false;
  };

  useEffect(() => {
    setSelectedAnswersClientTransient(selectedAnswers);
  }, [selectedAnswers, setSelectedAnswersClientTransient]);

  useEffect(() => {
    if (errorMessage) {
      setSelectedAnswersClientTransient([]);
    }
  }, [errorMessage, inputEntry, selectedAnswers, setSelectedAnswersClientTransient]);

  if (isServerSide() || (!window?.google?.maps && inputType === ANSWER_SELECTOR_TYPES.input_address)) {
    return null;
  }

  return (
    <Form classes="width100 marginAuto">
      {inputType === ANSWER_SELECTOR_TYPES.input_address ? (
        <Form.Row classes="width100 marginAuto paddingLeft-small paddingRight-small">
          <Form.Column sm={4} mdOffset={2} md={4} lg={4} lgOffset={4}>
            <AddressAutocomplete value={inputEntry} onChange={(e: string) => onHandleInputChange(e)} label="Address" error={errorMessage} />
          </Form.Column>
        </Form.Row>
      ) : (
        <Form.Row classes="width100 marginAuto">
          <Form.Column sm={4} mdOffset={2} md={4} lg={4} lgOffset={4}>
            <div className={cn(styles.inputSelector, 'flex alignItemsCenter')}>
              <Maybe condition={inputType === ANSWER_SELECTOR_TYPES.input_currency}>
                <p className="p1 n700 paddingRight-tiny1 flex alignSelfFlexStart paddingTop-small">$</p>
              </Maybe>
              <InputField
                type="text"
                value={inputEntry}
                error={errorMessage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target;
                  onHandleInputChange(value);
                }}
                containerClass="flex1"
              />
            </div>
          </Form.Column>
        </Form.Row>
      )}
    </Form>
  );
};

export default React.memo(RegularInputSelector);
